import BoltIcon from "@mui/icons-material/Bolt";
import HealthAndSafetyIcon from "@mui/icons-material/HealthAndSafety";
import PriorityHighIcon from "@mui/icons-material/PriorityHigh";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { Box, Skeleton } from "@mui/material";
import { useState } from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import { EMPTY_FLEET_SELECTION, type FleetSelection } from "../api/dashboard";
import CurrentVsResidualCard from "../components/CurrentVsResidualCard";
import FleetFilterBar from "../components/FleetFilterBar";
import ImplementationPhaseCard from "../components/ImplementationPhaseCard";
import KpiCard from "../components/KpiCard";
import PlantSnapshotCard from "../components/PlantSnapshotCard";
import RegionIndicator from "../components/RegionIndicator";
import SpofTable from "../components/SpofTable";
import {
  useDashboardPlants,
  usePlantOverview,
} from "../hooks/usePlantOverview";
import PageHeader from "../layout/PageHeader";
import { mw } from "../utils/format";
import { regionLabel } from "../utils/regions";

export default function PlantOverviewPage() {
  const { plantId } = useParams<{ plantId: string }>();
  const navigate = useNavigate();
  const [selection, setSelection] = useState<FleetSelection>({
    ...EMPTY_FLEET_SELECTION,
    power_plant_id: plantId ?? null,
  });

  const plants = useDashboardPlants().data ?? [];
  const overview = usePlantOverview(plantId ?? null, selection).data;

  /** Region and plant pick which plant is displayed; the other two selectors filter its register. */
  function select(next: FleetSelection) {
    const inRegion = (overview?.filters.plants ?? []).filter(
      (plant) => !next.region || plant.region === next.region,
    );
    const target = inRegion.some((plant) => plant.id === next.power_plant_id)
      ? next.power_plant_id
      : (inRegion[0]?.id ?? null);
    setSelection({ ...next, power_plant_id: target });
    if (target && target !== plantId) {
      navigate(`/dashboard/plants/${target}`);
    }
  }

  if (!plantId) {
    return plants.length === 0 ? (
      <Skeleton variant="rounded" height={520} />
    ) : (
      <Navigate to={`/dashboard/plants/${plants[0].id}`} replace />
    );
  }

  return (
    <Box>
      <PageHeader
        title={
          overview ? `${overview.plant.name} - SPOF Risk Overview` : "Overview"
        }
        trail={[
          { label: "Fleet overview", to: "/dashboard" },
          ...(overview?.plant.region
            ? [{ label: regionLabel(overview.plant.region) }]
            : []),
          { label: overview?.plant.name ?? "Plant" },
        ]}
      />

      {!overview ? (
        <Skeleton variant="rounded" height={520} />
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
          <RegionIndicator region={overview.plant.region} />

          <FleetFilterBar
            options={overview.filters}
            selection={selection}
            onChange={select}
            keys={["power_plant_id", "risk_category", "spof_owner"]}
          />

          <Box
            sx={{
              display: "grid",
              gap: 2,
              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(2, 1fr)",
                lg: "repeat(4, 1fr)",
              },
            }}
          >
            <KpiCard
              label="Confirmed SPOFs"
              value={overview.kpis.confirmed_spofs}
              color="#E53935"
              icon={WarningAmberIcon}
            />
            <KpiCard
              label="High & Severe"
              value={overview.kpis.high_and_severe}
              color="#F57C00"
              icon={PriorityHighIcon}
            />
            <KpiCard
              label="Highest Exposure"
              value={mw(overview.kpis.highest_exposure_mw)}
              suffix=" MW"
              color="#1AA5A0"
              icon={BoltIcon}
            />
            <KpiCard
              label="Residual High & Severe"
              value={overview.kpis.residual_high_and_severe}
              color="#1B6FD4"
              icon={HealthAndSafetyIcon}
            />
          </Box>

          <Box
            sx={{
              display: "grid",
              gap: 2,
              gridTemplateColumns: { xs: "1fr", lg: "2fr 3fr 2fr" },
            }}
          >
            <PlantSnapshotCard snapshot={overview.snapshot} />
            <CurrentVsResidualCard
              baseline={overview.baseline_risk}
              residual={overview.residual_risk}
            />
            <ImplementationPhaseCard phases={overview.implementation_phases} />
          </Box>

          <SpofTable plantId={plantId} spofs={overview.spofs} />
        </Box>
      )}
    </Box>
  );
}
